import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedRoute } from './auth/ProtectedRoute';
import { RequireOnboarding } from './auth/RequireOnboarding';
import { RequirePurchase } from './auth/RequirePurchase';
import { FullPageLoader } from './components/ui/FullPageLoader';
import { AppShell } from './layout/AppShell';
import { PublicLayout } from './layout/PublicLayout';
import { DashboardPage } from './pages/DashboardPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';

// The public marketing site isn't behind auth and is most visitors' first
// paint, so it stays eager (HomePage) or lazy-but-adjacent (About/Packages/
// Contact — one click away, not first paint).
const WhatsNewPage = lazy(() =>
  import('./pages/WhatsNewPage').then((m) => ({ default: m.WhatsNewPage })),
);
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const PublicPackagesPage = lazy(() =>
  import('./pages/PublicPackagesPage').then((m) => ({ default: m.PublicPackagesPage })),
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })),
);

// Report/Account/Onboarding/NotFound aren't hit on first paint, so they're
// code-split; Dashboard/Login stay eager since they're reached immediately.
const ReportPage = lazy(() =>
  import('./pages/ReportPage').then((m) => ({ default: m.ReportPage })),
);
const AccountPage = lazy(() =>
  import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const EditProfilePage = lazy(() =>
  import('./pages/EditProfilePage').then((m) => ({ default: m.EditProfilePage })),
);
const OnboardingPage = lazy(() =>
  import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const PackageSelectionPage = lazy(() =>
  import('./pages/PackageSelectionPage').then((m) => ({ default: m.PackageSelectionPage })),
);
const QuestionnairesPage = lazy(() =>
  import('./pages/QuestionnairesPage').then((m) => ({ default: m.QuestionnairesPage })),
);
const QuestionnaireFillPage = lazy(() =>
  import('./pages/QuestionnaireFillPage').then((m) => ({ default: m.QuestionnaireFillPage })),
);
const MyBookingsPage = lazy(() =>
  import('./pages/MyBookingsPage').then((m) => ({ default: m.MyBookingsPage })),
);
const FingerprintsPage = lazy(() =>
  import('./pages/FingerprintsPage').then((m) => ({ default: m.FingerprintsPage })),
);
const ResourcesPage = lazy(() =>
  import('./pages/ResourcesPage').then((m) => ({ default: m.ResourcesPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Public marketing site (P21-T02) — no auth, anyone's first paint. */}
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route
          path="about"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <AboutPage />
            </Suspense>
          }
        />
        <Route
          path="packages"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <PublicPackagesPage />
            </Suspense>
          }
        />
        <Route
          path="contact"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <ContactPage />
            </Suspense>
          }
        />
      </Route>

      {/* The authenticated client app, entirely under /app — requires an
          authenticated client (P21-T02: moved off `/` to free it for the
          public marketing site above). */}
      <Route path="app" element={<ProtectedRoute />}>
        {/* Full-screen, outside AppShell — no tab bar/side-nav while
            onboarding is incomplete (CP-P1-T03). */}
        <Route
          path="onboarding"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <OnboardingPage />
            </Suspense>
          }
        />
        <Route element={<RequireOnboarding />}>
          {/* Full-screen, outside AppShell — both the RequirePurchase
              redirect target (no verified purchase yet) and the
              already-verified client's "Change Package" entry point. */}
          <Route
            path="packages"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <PackageSelectionPage />
              </Suspense>
            }
          />
          <Route element={<RequirePurchase />}>
            <Route element={<AppShell />}>
              <Route index element={<DashboardPage />} />
              <Route
                path="report"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <ReportPage />
                  </Suspense>
                }
              />
              <Route
                path="questionnaires"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <QuestionnairesPage />
                  </Suspense>
                }
              />
              <Route
                path="questionnaires/:id"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <QuestionnaireFillPage />
                  </Suspense>
                }
              />
              <Route
                path="whats-new"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <WhatsNewPage />
                  </Suspense>
                }
              />
              <Route
                path="account"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <AccountPage />
                  </Suspense>
                }
              />
              <Route
                path="account/edit"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <EditProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="fingerprints"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <FingerprintsPage />
                  </Suspense>
                }
              />
              <Route
                path="resources"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <ResourcesPage />
                  </Suspense>
                }
              />
              {/* Booking is now a modal on `/app/bookings` (triggered from
                MyBookingsPage), not its own page — redirect old links. */}
              <Route path="book" element={<Navigate to="/app/bookings" replace />} />
              <Route
                path="bookings"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <MyBookingsPage />
                  </Suspense>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <NotFoundPage />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
